// PRD.330 — Clinic Collection Details: SQL field mapping + QuerySpec builders.
//
// One row = one `invoice_payments` payment event (D2). Report date is
// `ip.paid_at` (D3). Invoice-less (patient-level) payments are INCLUDED via
// LEFT JOIN — no predicate may require an invoice (D13). Patient resolves
// through the invoice when present, else `ip.patient_id` (D8), mirroring the
// existing join in `qHomeCollectionsDetail`. Amount is `ip.amount_cents`,
// never derived from invoice totals/status (D10). No refunds/adjustments (D15).

import {
  CLINIC_COLLECTIONS_REPORT,
  buildReportTimeBuckets,
  localDayRangeToUtcBounds,
  selectReportTimeBucket,
} from '@helios/domain';
import type { ClinicCollectionsColumnId, ReportQuery, ReportUtcBounds } from '@helios/domain';
import {
  buildReportFacetSql,
  buildReportGroupedSql,
  buildReportRowsSql,
  buildReportTimeSeriesSql,
  buildReportSummarySql,
  reportQueryKeyParts,
  type ReportSqlSource,
} from './report-sql-logic.js';

const PATIENT_NAME = 'COALESCE(p.full_name, i.patient_name)';

export const CLINIC_COLLECTIONS_SQL_SOURCE: ReportSqlSource<ClinicCollectionsColumnId> = {
  definition: CLINIC_COLLECTIONS_REPORT,
  from: `invoice_payments ip
LEFT JOIN invoices i ON i.id = ip.invoice_id
LEFT JOIN patients p ON p.id = COALESCE(i.patient_id, ip.patient_id)`,
  dateColumn: 'ip.paid_at',
  fields: {
    paymentDate: { expr: 'ip.paid_at', sortExpr: 'julianday(ip.paid_at)' },
    patient: { expr: PATIENT_NAME, sortExpr: `${PATIENT_NAME} COLLATE NOCASE` },
    invoiceNo: { expr: 'i.invoice_no', sortExpr: 'i.invoice_no COLLATE NOCASE', nullsLast: true },
    method: { expr: 'ip.method', sortExpr: 'ip.method COLLATE NOCASE' },
    amount: { expr: 'ip.amount_cents' },
    notes: { expr: 'ip.notes' },
  },
  // Dialog-entered payments share a local-noon `paid_at` per day, so order
  // same-day rows by recording time, then id, for stable pagination.
  tiebreak: ['julianday(ip.created_at)', 'ip.id'],
};

/**
 * Structurally identical to `QuerySpec` from @helios/powersync-client-core.
 * Declared locally so this module compiles under the node:test tsconfig
 * (Node16 resolution cannot load powersync-client-core's extensionless
 * source); values are passed straight to `usePowerSyncQuery`.
 */
export interface ReportQuerySpec {
  key: readonly unknown[];
  sql: string;
  params: unknown[];
  watch: string[];
}

const WATCH = ['invoice_payments', 'invoices', 'patients'];
const KEY_BASE = ['reports', CLINIC_COLLECTIONS_REPORT.id] as const;

export interface ClinicCollectionRow {
  payment_id: string;
  paid_at: string;
  created_at: string | null;
  patient_id: string | null;
  patient_name: string | null;
  invoice_id: string | null;
  /** NULL for invoice-less (patient-level) payments — render as '—'. */
  invoice_no: string | null;
  method: string;
  amount_cents: number;
  notes: string | null;
}

export interface ClinicCollectionsSummaryRow {
  totalCount: number;
  /** SUM(invoice_payments.amount_cents) over ALL matching rows, in cents. */
  amountSum: number;
}

/** One category of a grouped visualization projection. */
export interface ClinicCollectionsByMethodRow {
  category: string | null;
  /** SUM(invoice_payments.amount_cents) for the category, in cents. */
  amountSum: number;
  paymentCount: number;
}

export interface ReportFacetRow {
  value: string | null;
  count: number;
}

const ROW_SELECT = `ip.id AS payment_id,
  ip.paid_at AS paid_at,
  ip.created_at AS created_at,
  COALESCE(i.patient_id, ip.patient_id) AS patient_id,
  ${PATIENT_NAME} AS patient_name,
  ip.invoice_id AS invoice_id,
  i.invoice_no AS invoice_no,
  ip.method AS method,
  ip.amount_cents AS amount_cents,
  ip.notes AS notes`;

type Query = ReportQuery<ClinicCollectionsColumnId>;

/** One page (50) of matching payment events, in the applied sort order. */
export function qClinicCollectionsRows(query: Query, bounds: ReportUtcBounds): ReportQuerySpec {
  const compiled = buildReportRowsSql(CLINIC_COLLECTIONS_SQL_SOURCE, query, bounds, ROW_SELECT);
  return {
    key: [
      ...KEY_BASE,
      'rows',
      reportQueryKeyParts(CLINIC_COLLECTIONS_SQL_SOURCE, query, bounds, {
        includePage: true,
        includeSort: true,
      }),
    ],
    sql: compiled.sql,
    params: compiled.params,
    watch: WATCH,
  };
}

/** Matching count + Total Collection Amount over the full filtered result. */
export function qClinicCollectionsSummary(query: Query, bounds: ReportUtcBounds): ReportQuerySpec {
  const compiled = buildReportSummarySql(CLINIC_COLLECTIONS_SQL_SOURCE, query, bounds);
  return {
    key: [
      ...KEY_BASE,
      'summary',
      reportQueryKeyParts(CLINIC_COLLECTIONS_SQL_SOURCE, query, bounds, {
        includePage: false,
        includeSort: false,
      }),
    ],
    sql: compiled.sql,
    params: compiled.params,
    watch: WATCH,
  };
}

/** Payment Method facet: every applied constraint except the method filter itself. */
export function qClinicCollectionsMethodFacet(
  query: Query,
  bounds: ReportUtcBounds,
): ReportQuerySpec {
  const compiled = buildReportFacetSql(CLINIC_COLLECTIONS_SQL_SOURCE, query, bounds, 'method');
  return {
    key: [
      ...KEY_BASE,
      'facet:method',
      reportQueryKeyParts(CLINIC_COLLECTIONS_SQL_SOURCE, query, bounds, {
        includePage: false,
        includeSort: false,
        omitFilterFor: 'method',
      }),
    ],
    sql: compiled.sql,
    params: compiled.params,
    watch: WATCH,
  };
}

/**
 * PRD.330 Phase 4 — Collections by Payment Method: amount + payment count
 * per `ip.method` under the FULL applied query (the method filter applies
 * too). Page and sort are excluded from the key, so paging/sorting never
 * re-run it.
 */
export function qClinicCollectionsByMethod(query: Query, bounds: ReportUtcBounds): ReportQuerySpec {
  const compiled = buildReportGroupedSql(
    CLINIC_COLLECTIONS_SQL_SOURCE,
    query,
    bounds,
    'collectionsByMethod',
  );
  return {
    key: [
      ...KEY_BASE,
      'viz:collectionsByMethod',
      reportQueryKeyParts(CLINIC_COLLECTIONS_SQL_SOURCE, query, bounds, {
        includePage: false,
        includeSort: false,
      }),
    ],
    sql: compiled.sql,
    params: compiled.params,
    watch: WATCH,
  };
}

/** One bucket of Collections Over Time. */
export interface ClinicCollectionsOverTimeRow {
  /** Day 'YYYY-MM-DD', week-start Monday 'YYYY-MM-DD', or month 'YYYY-MM'. */
  bucket: string | null;
  amountSum: number;
  paymentCount: number;
}

/**
 * PRD.330 Phase 5 — Collections Over Time: amount + payment count per
 * calendar bucket of `paid_at`. The bucket (day/week/month, or none for a
 * one-day range) is chosen from `query.dateRange` alone; bucket bounds use
 * the same local-day conversion as the date filter. Full applied query; page
 * and sort are excluded from the key.
 */
export function qClinicCollectionsOverTime(query: Query, bounds: ReportUtcBounds): ReportQuerySpec {
  const bucket = selectReportTimeBucket(query.dateRange);
  const buckets = bucket
    ? buildReportTimeBuckets(query.dateRange, bucket).map((b) => ({
        key: b.key,
        ...localDayRangeToUtcBounds({ from: b.from, to: b.to }),
      }))
    : [];
  const compiled = buildReportTimeSeriesSql(
    CLINIC_COLLECTIONS_SQL_SOURCE,
    query,
    bounds,
    'collectionsOverTime',
    buckets,
  );
  return {
    key: [
      ...KEY_BASE,
      'viz:collectionsOverTime',
      bucket ?? 'none',
      reportQueryKeyParts(CLINIC_COLLECTIONS_SQL_SOURCE, query, bounds, {
        includePage: false,
        includeSort: false,
      }),
    ],
    sql: compiled.sql,
    params: compiled.params,
    watch: WATCH,
  };
}
